import { useState } from 'react';

interface HeaderProps {
  scrolled: boolean;
}

export default function Header({ scrolled }: HeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  const menuItems = [
    { label: 'Inicio', href: '#inicio' },
    { label: 'Nuestra Cafetería', href: '#cafeteria' },
    { label: 'Carta', href: '#carta' },
    { label: 'Catálogo Ropa', href: '#ropa' },
    { label: 'Ubicación', href: '#ubicacion' },
    { label: 'Contacto', href: '#contacto' }
  ];

  const handleMenuClick = (href: string) => {
    setMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const whatsappNumber = '+56994347092';
  const whatsappMessage = 'Hola, me gustaría hacer una consulta sobre la cafetería';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#inicio" className="flex items-center gap-3" onClick={(e) => {
            e.preventDefault();
            handleMenuClick('#inicio');
          }}>
            <div className={`w-12 h-12 flex items-center justify-center transition-colors ${scrolled ? 'text-emerald-800' : 'text-white'}`}>
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Montañas de fondo */}
                <path d="M2 28L12 18L18 24L24 18L30 24L36 18L46 28" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                
                {/* Casona/Cabaña */}
                <path d="M14 32L24 24L34 32V42H14V32Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M19 42V36H29V42" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <rect x="18" y="28" width="4" height="4" stroke="currentColor" strokeWidth="1.5"/>
                <rect x="26" y="28" width="4" height="4" stroke="currentColor" strokeWidth="1.5"/>
                
                {/* Taza de café (detalle sutil) */}
                <path d="M38 34H40C41.1046 34 42 34.8954 42 36C42 37.1046 41.1046 38 40 38H38" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                <path d="M34 34H38V40C38 41.1046 37.1046 42 36 42C34.8954 42 34 41.1046 34 40V34Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M35 32C35 32 35.5 30 36 30C36.5 30 37 32 37 32" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="flex flex-col">
              <span className={`font-bold text-base sm:text-lg transition-colors ${scrolled ? 'text-stone-800' : 'text-white'}`}>
                Cafetería Boutique La Casona
              </span>
              <span className={`text-xs transition-colors ${scrolled ? 'text-stone-600' : 'text-white/90'}`}>
                Coyhaique · Patagonia Chilena
              </span>
            </div>
          </a>

          {/* Desktop Menu */}
          <nav className="hidden lg:flex items-center gap-6">
            {menuItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleMenuClick(item.href);
                }}
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  scrolled ? 'text-stone-700 hover:text-emerald-700' : 'text-white hover:text-emerald-200'
                }`}
              >
                {item.label}
              </a>
            ))}
            <a
              href={`https://wa.me/56994347092?text=${encodeURIComponent(whatsappMessage)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 rounded-full text-sm font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line mr-2"></i>
              WhatsApp
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-3 lg:hidden">
            <a
              href={`https://wa.me/56994347092?text=${encodeURIComponent(whatsappMessage)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-full text-sm font-semibold transition-all shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line"></i>
            </a>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className={`p-2 rounded-lg transition-colors cursor-pointer ${
                scrolled ? 'text-stone-800 hover:bg-stone-100' : 'text-white hover:bg-white/10'
              }`}
              aria-label="Menú"
            >
              <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden bg-white border-t border-stone-200 py-4 shadow-lg">
            <nav className="flex flex-col gap-2">
              {menuItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => {
                    e.preventDefault();
                    handleMenuClick(item.href);
                  }}
                  className="px-4 py-3 text-stone-700 hover:bg-stone-50 font-medium transition-colors cursor-pointer"
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
